<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsText from '../vision/VsText.vue'

const MIN_REPEAT_JOB_COUNT = 2
const MAX_REPEAT_JOB_COUNT = 10

const props = withDefaults(
  defineProps<{
    count: number
    minCount?: number
    isRepeatTrip?: boolean
    allowMultiService: boolean
  }>(),
  {
    minCount: 1,
    isRepeatTrip: true,
  },
)

const emit = defineEmits<{
  (e: 'update:count', value: number): void
  (e: 'update:isRepeatTrip', value: boolean): void
}>()

const cachedMultiJobCount = ref<number | null>(null)
const cachedRepeatJobSelection = ref<boolean | null>(null)

const hasMinCountOverride = computed(() => props.minCount > 1)

const effectiveMin = computed(() =>
  Math.max(props.minCount ?? MIN_REPEAT_JOB_COUNT, MIN_REPEAT_JOB_COUNT),
)

const localCount = computed({
  get: () => props.count,
  set: (value: number) => emit('update:count', value),
})

const isLocalRepeatJob = computed({
  get: () => props.isRepeatTrip,
  set: (value: boolean) => emit('update:isRepeatTrip', value),
})

const allowFieldWorkerToChooseFollowUpActivity = computed({
  get: () => !isLocalRepeatJob.value,
  set: (value: boolean) => {
    isLocalRepeatJob.value = !value
  },
})

const isMultiJob = computed(() => localCount.value >= MIN_REPEAT_JOB_COUNT)

const selectSingleJob = () => {
  if (hasMinCountOverride.value && isMultiJob.value) return

  cachedMultiJobCount.value = localCount.value
  cachedRepeatJobSelection.value = isLocalRepeatJob.value

  localCount.value = 1
  isLocalRepeatJob.value = true
}

const selectMultiJob = () => {
  if (!isMultiJob.value) {
    localCount.value =
      cachedMultiJobCount.value != null
        ? Math.max(cachedMultiJobCount.value, effectiveMin.value)
        : effectiveMin.value

    if (cachedRepeatJobSelection.value != null) {
      isLocalRepeatJob.value = cachedRepeatJobSelection.value
    } else {
      isLocalRepeatJob.value = true
    }

    cachedMultiJobCount.value = null
    cachedRepeatJobSelection.value = null
  }
}

watch(
  () => props.allowMultiService,
  async (newVal) => {
    if (newVal === false) {
      await nextTick()
      allowFieldWorkerToChooseFollowUpActivity.value = false
    }
  },
  { immediate: true },
)

const increment = () => {
  if (localCount.value < MAX_REPEAT_JOB_COUNT) {
    localCount.value += 1
  }
}

const decrement = () => {
  if (localCount.value > effectiveMin.value) {
    localCount.value -= 1
  }
}
</script>

<template>
  <div>
    <div>
      <div class="pill-selector" data-test="repeat-jobs-pill-selector">
        <div
          class="pill-selector-background"
          :style="{
            transform: `translateX(calc(${isMultiJob ? 1 : 0} * 100%))`,
          }"
        ></div>
        <button
          aria-label="Single job"
          style="flex: 1"
          :disabled="hasMinCountOverride && isMultiJob"
          :class="{
            'pill-button-disabled': hasMinCountOverride && isMultiJob,
          }"
          @click="selectSingleJob"
        >
          <vs-text :class="['pill-option', { active: !isMultiJob }]"> Single job </vs-text>
        </button>
        <button aria-label="Multiple jobs" style="flex: 1" @click="selectMultiJob">
          <vs-text type="header" :class="['pill-option', { active: isMultiJob }]">
            Multiple jobs
          </vs-text>
        </button>
      </div>
    </div>

    <vs-wrapper>
      <div v-if="isMultiJob" class="pt-2">
        <div class="d-flex align-start">
          <div class="mr-1" style="width: 50%">
            <div
              class="d-flex align-center justify-space-between pa-1 mt-2"
              style="border: 1px solid #e1e1e1; border-radius: 9999px; width: 100%"
            >
              <v-btn
                icon
                :disabled="localCount <= effectiveMin"
                :class="{ 'decrement-disabled': localCount <= effectiveMin }"
                data-test="repeat-jobs-decrement"
                aria-label="Decrease repeat count"
                style="width: 44px; height: 44px"
                @click="decrement"
              >
                <v-icon
                  style="font-size: 16px"
                  :class="localCount > effectiveMin ? 'black--text' : 'grey--text'"
                >
                  mdi-minus
                </v-icon>
              </v-btn>

              <div class="py-3 no-select" data-test="repeat-jobs-count">
                <vs-text bold>{{ localCount }} total</vs-text>
              </div>

              <v-btn
                icon
                :disabled="localCount >= MAX_REPEAT_JOB_COUNT"
                data-test="repeat-jobs-increment"
                aria-label="Increase repeat count"
                style="width: 44px; height: 44px"
                @click="increment"
              >
                <v-icon
                  style="font-size: 16px"
                  :class="localCount < MAX_REPEAT_JOB_COUNT ? 'black--text' : 'grey--text'"
                >
                  mdi-plus
                </v-icon>
              </v-btn>
            </div>
          </div>

          <div class="d-flex align-items-center mt-2" style="width: 50%">
            <div
              class="font-weight-medium grey--text text--darken-1 py-1 no-select"
              style="border: 1px solid #e1e1e1; border-radius: 9999px; flex-grow: 1"
            >
              <div class="flex-column" style="text-align: center">
                <vs-text> Must be completed </vs-text>
                <vs-text> within a <span class="bold">24 hr</span> period </vs-text>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-4" data-test="repeat-service-checkbox-selector">
          <v-checkbox
            v-if="allowMultiService"
            v-model="allowFieldWorkerToChooseFollowUpActivity"
            data-test="repeat-service-checkbox"
            dense
          >
            <template #label>
              <vs-text type="caption">Field worker can choose follow-up activity</vs-text>
            </template>
          </v-checkbox>
        </div>
      </div>
    </vs-wrapper>
  </div>
</template>

<style scoped>
.no-select {
  -webkit-user-select: none;
  user-select: none;
}

.pill-selector {
  display: flex;
  position: relative;
  width: 100%;
  background-color: #f3f4f6;
  border-radius: 9999px;
  padding: 4px;
  gap: 0;
}

.pill-selector-background {
  position: absolute;
  top: 4px;
  left: 4px;
  width: calc(50% - 4px);
  height: calc(100% - 8px);
  background-color: white;
  border-radius: 9999px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.35s cubic-bezier(0.25, 1.25, 0.75, 1);
  z-index: 1;
  pointer-events: none;
}

.pill-option {
  position: relative;
  z-index: 2;
  flex: 1;
  padding: 8px;
  background-color: transparent;
  color: #434347;
  border-radius: 9999px;
  cursor: pointer;
  transition: color 0.2s ease;
}

.pill-option.active {
  color: #085fd4;
  font-weight: bold;
}

.pill-option:hover:not(.active) {
  color: #5c636f;
}

.pill-button {
  position: relative;
  z-index: 2;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
}

.decrement-disabled {
  opacity: 0.4 !important;
  cursor: not-allowed !important;
}

.pill-button-disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
